import { useCallback, useEffect, useState } from "react";
import { Loader2, MapPin, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import { authFetch } from "../../lib/authFetch";

interface DeliveryAddress {
  id: string;
  label: string | null;
  adresse: string;
  codePostal: string | null;
  ville: string | null;
  pays: string | null;
  contactNom: string | null;
  contactTelephone: string | null;
  isDefault: boolean;
}
type AddressForm = Omit<DeliveryAddress, "id">;

const emptyForm: AddressForm = {
  label: "", adresse: "", codePostal: "", ville: "", pays: "France",
  contactNom: "", contactTelephone: "", isDefault: false,
};
const inputClass = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary/50";

export default function DeliveryAddressesSection({ isAdmin }: { isAdmin: boolean }) {
  const [addresses, setAddresses] = useState<DeliveryAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AddressForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch("/api/espace-client/my/addresses");
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Impossible de charger les adresses.");
      setAddresses(data);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible de charger les adresses.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (address?: DeliveryAddress) => {
    setEditingId(address?.id || "new");
    setForm(address ? {
      label: address.label || "", adresse: address.adresse || "", codePostal: address.codePostal || "",
      ville: address.ville || "", pays: address.pays || "", contactNom: address.contactNom || "",
      contactTelephone: address.contactTelephone || "", isDefault: address.isDefault,
    } : { ...emptyForm });
    setError(null);
  };
  const cancelEdit = () => { setEditingId(null); setError(null); };
  const set = (key: keyof AddressForm, value: string | boolean) => setForm(current => ({ ...current, [key]: value }));

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const editing = editingId !== "new";
      const response = await authFetch(editing ? `/api/espace-client/my/addresses/${editingId}` : "/api/espace-client/my/addresses", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Enregistrement de l’adresse impossible.");
      setEditingId(null);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Enregistrement de l’adresse impossible.");
    } finally { setSaving(false); }
  };

  const setDefault = async (id: string) => {
    setError(null);
    try {
      const response = await authFetch(`/api/espace-client/my/addresses/${id}/default`, { method: "PUT" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Impossible de définir l’adresse par défaut.");
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Impossible de définir l’adresse par défaut."); }
  };

  const remove = async (address: DeliveryAddress) => {
    if (!window.confirm(`Supprimer l’adresse « ${address.label || address.adresse} » ?`)) return;
    setError(null);
    try {
      const response = await authFetch(`/api/espace-client/my/addresses/${address.id}`, { method: "DELETE" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Suppression impossible.");
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Suppression impossible."); }
  };

  return (
    <section className="bg-card border border-border/60 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-border/50">
        <div className="flex items-center gap-2.5"><MapPin className="w-4 h-4 text-muted-foreground" /><h2 className="text-sm font-semibold">Adresses de livraison</h2><span className="text-xs text-muted-foreground">({addresses.length})</span></div>
        {isAdmin && !editingId && <button type="button" onClick={() => startEdit()} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"><Plus className="w-3.5 h-3.5" />Ajouter une adresse</button>}
      </div>
      {error && <div className="mx-5 mt-4 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert">{error}</div>}
      {editingId && isAdmin && <form onSubmit={save} className="m-5 rounded-xl border border-border bg-background p-4 space-y-4">
        <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">{editingId === "new" ? "Nouvelle adresse" : "Modifier l’adresse"}</h3><button type="button" onClick={cancelEdit} aria-label="Fermer le formulaire" className="rounded p-1 text-muted-foreground hover:bg-secondary"><X className="w-4 h-4" /></button></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AddressField label="Libellé *" value={form.label || ""} onChange={value => set("label", value)} required placeholder="Siège social, entrepôt…" />
          <AddressField label="Adresse *" value={form.adresse} onChange={value => set("adresse", value)} required placeholder="Rue et numéro" />
          <AddressField label="Code postal *" value={form.codePostal || ""} onChange={value => set("codePostal", value)} required />
          <AddressField label="Ville *" value={form.ville || ""} onChange={value => set("ville", value)} required />
          <AddressField label="Pays *" value={form.pays || ""} onChange={value => set("pays", value)} required />
          <AddressField label="Contact sur place" value={form.contactNom || ""} onChange={value => set("contactNom", value)} />
          <AddressField label="Téléphone du contact" value={form.contactTelephone || ""} onChange={value => set("contactTelephone", value)} type="tel" />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isDefault} onChange={event => set("isDefault", event.target.checked)} className="rounded border-border text-primary focus:ring-primary" />Définir comme adresse par défaut</label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={cancelEdit} className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary">Annuler</button>
          <button type="submit" disabled={saving || !form.adresse.trim()} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />}{saving ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </form>}
      <div className="p-5">
        {loading ? <div className="flex justify-center py-7" role="status"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> :
          addresses.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Aucune adresse de livraison enregistrée.</p> :
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {addresses.map(address => <article key={address.id} className="rounded-lg border border-border bg-background p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium">{address.label || "Adresse de livraison"}</h3>
                    <p className="mt-1 text-sm">{address.adresse}</p>
                    <p className="text-xs text-muted-foreground">{[address.codePostal, address.ville].filter(Boolean).join(" ")}{address.pays ? ` · ${address.pays}` : ""}</p>
                  </div>
                  {address.isDefault && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"><Star className="w-3 h-3" />Par défaut</span>}
                </div>
                {(address.contactNom || address.contactTelephone) && <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">{[address.contactNom, address.contactTelephone].filter(Boolean).join(" · ")}</p>}
                {isAdmin && <div className="mt-3 flex flex-wrap gap-3 border-t border-border pt-3">
                  {!address.isDefault && <button type="button" onClick={() => setDefault(address.id)} className="text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Définir par défaut</button>}
                  <button type="button" onClick={() => startEdit(address)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"><Pencil className="w-3.5 h-3.5" />Modifier</button>
                  <button type="button" onClick={() => remove(address)} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive"><Trash2 className="w-3.5 h-3.5" />Supprimer</button>
                </div>}
              </article>)}
            </div>}
      </div>
    </section>
  );
}

function AddressField({ label, value, onChange, placeholder, type = "text", required = false }: {
  label: string; value: string; onChange: (value: string) => void; placeholder?: string; type?: string; required?: boolean;
}) {
  return <label className="block text-xs font-medium text-muted-foreground">{label}<input className={`${inputClass} mt-1.5`} type={type} value={value} required={required} onChange={event => onChange(event.target.value)} placeholder={placeholder} /></label>;
}